import { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav.jsx';
import PublicFooter from '../components/PublicFooter.jsx';
import LobsterMascot from '../components/LobsterMascot.jsx';
import PageMeta from '../components/PageMeta.jsx';
import { useTranslation } from '../i18n/I18nContext.jsx';
import { useLocalizedPath } from '../hooks/useLocalizedPath.js';
import { usePrices, formatPrice } from '../hooks/usePrices.js';

export default function PricingPage() {
  const { t } = useTranslation();
  const lp = useLocalizedPath();
  const included = t('pricing.included');
  const whatsIncluded = t('pricing.whatsIncluded.items');
  const faqs = t('pricing.faq.items');
  const { monthly, annual, loading: pricesLoading } = usePrices();
  const [billingCycle, setBillingCycle] = useState('month');

  const selected = billingCycle === 'year' ? annual : monthly;

  return (
    <>
      <PageMeta
        title={t('pricing.meta.title')}
        description={t('pricing.meta.description')}
        path="/pricing"
      />

      <div className="pub-page">
        <PublicNav />

        <main id="main-content">
        {/* ── Header ── */}
        <section className="page-hero">
          <div className="section-inner" style={{ textAlign: 'center' }}>
            <div className="section-label">{t('pricing.header.label')}</div>
            <h1 className="page-hero-title">{t('pricing.header.title')}</h1>
            <p className="page-hero-sub">{t('pricing.header.sub')}</p>
          </div>
        </section>

        {/* ── Pricing card ── */}
        <section className="section" aria-labelledby="pricing-plan-name">
          <div className="section-inner pricing-card-wrap">
            <div className="pricing-card">
              <div className="pricing-card-header">
                <LobsterMascot size={72} aria-hidden="true" />
                <div>
                  <div id="pricing-plan-name" className="pricing-plan-name">{t('pricing.card.planName')}</div>
                  <div className="pricing-plan-desc">{t('pricing.card.planDesc')}</div>
                </div>
              </div>

              {/* ── Billing cycle toggle ── */}
              {annual && (
                <div className="pricing-toggle" role="radiogroup" aria-label="Billing cycle">
                  <button
                    role="radio"
                    aria-checked={billingCycle === 'month'}
                    className={`pricing-toggle-btn${billingCycle === 'month' ? ' active' : ''}`}
                    onClick={() => setBillingCycle('month')}
                  >
                    {t('pricing.toggle.monthly')}
                  </button>
                  <button
                    role="radio"
                    aria-checked={billingCycle === 'year'}
                    className={`pricing-toggle-btn${billingCycle === 'year' ? ' active' : ''}`}
                    onClick={() => setBillingCycle('year')}
                  >
                    {t('pricing.toggle.annual')}
                    {annual.savings > 0 && (
                      <span className="pricing-savings-badge">
                        {t('pricing.toggle.save', { pct: annual.savings })}
                      </span>
                    )}
                  </button>
                </div>
              )}

              <div className="pricing-card-price">
                {pricesLoading ? (
                  <div className="spinner-sm" role="status" aria-label="Loading prices" />
                ) : selected ? (
                  <>
                    <div className="pricing-price-row" aria-label={`Price: ${formatPrice(selected.amount)} per ${selected.interval === 'year' ? 'year' : 'month'}`}>
                      <span className="pricing-new-price">{formatPrice(selected.amount)}</span>
                      <span className="pricing-per">/{selected.interval === 'year' ? t('pricing.perYear') : t('pricing.perMonth')}</span>
                    </div>
                    {billingCycle === 'year' && monthly && (
                      <div className="pricing-effective">
                        {t('pricing.effectiveMonthly', { price: formatPrice(Math.round(selected.amount / 12)) })}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="pricing-price-row">
                    <span className="pricing-new-price">{t('pricing.card.priceNew')}</span>
                    <span className="pricing-per">{t('pricing.card.pricePer')}</span>
                  </div>
                )}
                <div className="pricing-promo-badge">{t('pricing.card.promoBadge')}</div>
              </div>

              <ul className="pricing-features" aria-label={t('pricing.card.includedAriaLabel')}>
                {Array.isArray(included) && included.map((item) => (
                  <li key={item} className="pricing-feature-item">
                    <span className="pricing-check" aria-hidden="true">✓</span>
                    {item}
                  </li>
                ))}
              </ul>

              <Link to={lp('/login')} className="btn btn-primary btn-lg pricing-cta">
                {t('pricing.card.cta')}
              </Link>
              <p className="pricing-cta-note">{t('pricing.card.ctaNote')}</p>
            </div>

            <div className="pricing-trust" aria-label={t('pricing.trust.ariaLabel')}>
              <div className="trust-item">
                <span className="trust-icon" aria-hidden="true">🔒</span>
                <span>{t('pricing.trust.stripe')}</span>
              </div>
              <div className="trust-item">
                <span className="trust-icon" aria-hidden="true">↩️</span>
                <span>{t('pricing.trust.cancel')}</span>
              </div>
              <div className="trust-item">
                <span className="trust-icon" aria-hidden="true">🛡️</span>
                <span>{t('pricing.trust.encrypted')}</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── What's included detail ── */}
        <section className="section section-alt" aria-labelledby="includes-title">
          <div className="section-inner">
            <div className="section-header">
              <h2 id="includes-title" className="section-title">{t('pricing.whatsIncluded.title')}</h2>
            </div>
            <div className="includes-grid">
              {Array.isArray(whatsIncluded) && whatsIncluded.map((item) => (
                <article key={item.title} className="include-card">
                  <div className="include-icon" aria-hidden="true">{item.icon}</div>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="section" aria-labelledby="faq-title">
          <div className="section-inner">
            <div className="section-header">
              <h2 id="faq-title" className="section-title">{t('pricing.faq.title')}</h2>
            </div>
            <div className="faq-list">
              {Array.isArray(faqs) && faqs.map((faq) => (
                <details key={faq.q} className="faq-item">
                  <summary className="faq-question">{faq.q}</summary>
                  <p className="faq-answer">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ── Bottom CTA ── */}
        <section className="section cta-section" aria-labelledby="pricing-cta-title">
          <div className="section-inner" style={{ textAlign: 'center' }}>
            <h2 id="pricing-cta-title" className="cta-title">{t('pricing.cta.title')}</h2>
            <p className="cta-sub">{t('pricing.cta.sub')}</p>
            <Link to={lp('/login')} className="btn btn-primary btn-lg">{t('pricing.cta.button')}</Link>
            <p style={{ marginTop: '0.75rem', color: 'var(--color-muted)', fontSize: '0.9rem' }}>
              {t('pricing.cta.questionsPrefix')} <Link to={lp('/contact')}>{t('pricing.cta.questionsLink')}</Link>
            </p>
          </div>
        </section>

        </main>
        <PublicFooter />
      </div>
    </>
  );
}
